/**
 * On-cell text contrast utility.
 *
 * Picks near-black or paper-white text for any heatmap cell based on
 * relative luminance (WCAG sRGB formula). Apply to every cell label
 * across the app — landing-page heatmap and the in-product heatmaps.
 *
 * Threshold 0.45 was tuned against the diverging ramp in the design
 * handoff; don't change it without re-testing every step in the ramp.
 *
 * Ported from the design handoff's `textOnCell.js` to TS so the rest
 * of the codebase can import it without a tsconfig allowJs flip.
 */

// Light-side ink. #0a0a0a (not the old near-black #000204) since both
// themes' positive hue is a very light yellow — a true-black label on it
// reads slightly harsh; #0a0a0a softens it without losing contrast.
const NEAR_BLACK = '#0a0a0a';
const PAPER_WHITE = '#f5f8fb';

/**
 * @param hex 6-digit hex like "#3aff8a" (with leading #).
 *            Returns near-black for light cells, paper-white for dark
 *            cells. Anything that's not a recognizable hex falls back
 *            to paper-white (safe on any plausible background).
 */
export function textOnCell(hex: string): string {
  if (typeof hex !== 'string' || hex.length < 7 || hex[0] !== '#') {
    return PAPER_WHITE;
  }
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return PAPER_WHITE;
  }
  const lin = (v: number) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.45 ? NEAR_BLACK : PAPER_WHITE;
}
